import { isAxiosError } from 'axios';
import pino, { type Logger as PinoLogger } from 'pino';
import { ZodError } from 'zod';
// Import from the platform-agnostic core, not `@sentry/nextjs`: the latter drags Next's global
// `ProcessEnv` type augmentation into this shared lib. `captureException`/`captureMessage` route to
// the same global client the app initialises via `@sentry/nextjs`.
import { captureException, captureMessage } from '@sentry/core';

import { rg4js } from './raygun';
import { extractAxiosError, extractZodError, formatError } from './formatError';
import { type LogLevel, type LogObject } from './logger.types';

const isDev = process.env['NODE_ENV'] !== 'production';
const logLevel = process.env['LOG_LEVEL'] || (isDev ? 'info' : 'warn');

/**
 * Allowlist serializer for the `error`/`err` fields. Bounds well-known error shapes so we never ship
 * unbounded `issues`, `aggregateErrors`, arbitrary Axios `response.data`, or sensitive Axios request
 * headers (e.g. Authorization) to fluent-bit. Runs for every log level — not just `error` — so an
 * AxiosError logged via `warn`/`info` is bounded too.
 *
 * Runs on the server only — in the browser we leave Error instances untouched so the Raygun
 * `transmit.send` path below can still detect them with `instanceof Error` and forward a real
 * Error to rg4js.
 */
const serializeError = (value: unknown) => {
  // Sometimes a string will be passed in as the err or error field to pino. When this happens, we
  // want to ensure the message is converted into an object so fluent-bit can properly handle it.
  if (typeof value === 'string') {
    return {
      message: value,
    };
  }
  if (globalThis.window !== undefined) {
    return value;
  }
  if (isAxiosError(value)) {
    return extractAxiosError(value);
  }
  if (value instanceof ZodError) {
    return extractZodError(value);
  }
  if (value instanceof Error) {
    return pino.stdSerializers.err(value);
  }
  return value;
};

// Since pino doesn't write to the console directly as part of its normal operation, we need to mock
// it in test.
const isTest =
  process.env['JEST_WORKER_ID'] !== undefined ||
  process.env['VITEST'] !== undefined;

const createPinoInstance = (): PinoLogger => {
  return pino(
    {
      level: logLevel,
      // In production, Next.js logs will be picked up by fluent-bit and sent to Kibana.
      // In tests, skip the pino-pretty transport: it runs in a worker thread that
      // writes directly to stdout, bypassing the custom destination below and any
      // test-time console suppression.
      transport:
        isDev && !isTest
          ? // For local dev, use a pretty-printer for readability
            { target: 'pino-pretty' }
          : undefined,
      // This manages how various keys of a logged object are serialized when they are ready to be
      // sent off.
      serializers: {
        err: serializeError,
        // Without this, errors may come through as just {}.
        error: serializeError,
      },
      // Pino's default message key is "msg". We want to use "message" instead to align w/ the rest
      // of our observability stack.
      messageKey: 'message',
      formatters: {
        // Use labels instead of numbers for the level field. By default, pino will use levels like
        // 30 for info, 40 for warn, and 50 for error. We prefer the strings.
        level: (label) => {
          return {
            level: label,
          };
        },
      },
      // If the code is running in the browser, we will forward logs to a custom transport that sends
      // them to Raygun.
      browser: {
        transmit: {
          // Controls the Raygun transmit below — it receives every level at or above info. The
          // Sentry dual-send further down is narrower (error/warn only); info is Raygun-only.
          level: logLevel,
          send: (level, { messages }) => {
            // In the Logger signature, we've ensured the only type of message that gets to the Logger
            // is a single LogObject.
            const [logObject] = messages as [LogObject];
            const { message, error, tags, ...customData } = logObject;

            // Raygun groups errors by `error.message`. The serializer leaves browser Error instances
            // untouched (see the `window` guard in serializeError), so without this a ZodError /
            // AxiosError would be grouped by its own `.message` — e.g. a ZodError's message is the
            // JSON-stringified `issues` blob — instead of the message we logged. Rebuild the Error so
            // our message wins while preserving the original name, stack, and custom enumerable
            // props. Error's `message`/`stack`/`name` are non-enumerable so Object.assign won't copy
            // them, but a subclass can declare `message` as an enumerable field — so set the
            // must-win fields explicitly, last.
            let errorWithUpdatedMessage: Error | undefined;
            if (error instanceof Error) {
              errorWithUpdatedMessage = new Error(message);
              Object.assign(errorWithUpdatedMessage, error);
              errorWithUpdatedMessage.stack = error.stack;
              errorWithUpdatedMessage.name = error.name;
              errorWithUpdatedMessage.message = message;
            }

            rg4js('send', {
              message,
              error: errorWithUpdatedMessage,
              tags,
              customData,
            });

            // No-op until the SDK is initialised (see sentry.client.config.ts), so no flag/DSN gate here.
            // Errors only: gate on level first so info logs never reach Sentry, even with an Error attached.
            if (level === 'error' || level === 'warn') {
              // Sentry tags are a key/value map, not an array — give each label its own key so it stays filterable.
              const sentryTags = tags?.length
                ? Object.fromEntries(tags.map((tag) => [tag, true]))
                : undefined;

              const captureContext = {
                ...(sentryTags ? { tags: sentryTags } : {}),
                extra: customData,
              };
              if (errorWithUpdatedMessage) {
                captureException(errorWithUpdatedMessage, captureContext);
              } else {
                captureMessage(message, {
                  level: level === 'warn' ? 'warning' : 'error',
                  ...captureContext,
                });
              }
            }
          },
        },
      },
    },
    // Only in test, we'll inject a custom destination that writes to the console. This is required
    // to test pino's output.
    isTest
      ? {
          write: (logObject) => {
            // eslint-disable-next-line no-console
            console.log(logObject);
          },
        }
      : undefined,
  );
};

const getSingletonPinoInstance = (() => {
  let instance: PinoLogger | null = null;

  return (): PinoLogger => {
    if (instance) {
      return instance;
    }

    instance = createPinoInstance();
    return instance;
  };
})();

const createLoggerForLevel =
  (level: LogLevel) =>
  (logObject: LogObject): void => {
    const pinoInstance = getSingletonPinoInstance();

    // Only errors need special formatting. Generally we use log and warn for simple messages and
    // data. It's only errors which will be varied and benefit from the formatting.
    if (level === 'error') {
      const formatted = formatError(level, logObject.message, logObject.error);
      pinoInstance[level]({
        ...logObject,
        ...formatted,
      });
    } else {
      pinoInstance[level](logObject);
    }
  };

/**
 * A logger backed by Pino which can be used to log anywhere in the app. It will automatically
 * forward logs to the right place depending on the environment.
 */
export const logger: Record<LogLevel, (logObject: LogObject) => void> = {
  info: createLoggerForLevel('info'),
  warn: createLoggerForLevel('warn'),
  error: createLoggerForLevel('error'),
};

export type Logger = typeof logger;
